import { useMutation, useQueryClient } from '@tanstack/react-query';

import { COHORTS_PATH } from '@verantos-dev/core-constants';
import type { CohortBuilderDefinitionLegacy } from '@verantos-dev/types-cohorts';
import type { ObjectResponse } from '@verantos-dev/types-common';

import { authFetch } from './fetchers';
import { invalidateCohort } from './invalidateCohort';
import { COHORT_WRITE_MUTATION_KEY } from './mutationKeys';

export interface DiscardDraftInput {
  projectId: string;
  cohortId: string;
}

/**
 * Server-reported state after discarding a draft. Mirrors the
 * `cohort_reverted` socket payload the legacy revert path returned.
 *
 * `definition` is the cohort that should now be displayed — the last
 * committed one when there is one, otherwise null. Callers hydrate the
 * Zustand store from this payload rather than waiting on a separate refetch
 * (the cache invalidation below is for any version/status round-trip).
 */
export interface DiscardDraftResponse {
  definition: CohortBuilderDefinitionLegacy | null;
  versionId?: string | null;
  newStatus: string;
}

/**
 * Canonical TanStack mutation for discarding a cohort draft (gen-3 path).
 * Replaces the legacy `useRevertCohortMutation` socket emit — see OR-6101.
 * Backed by `DELETE /v1/.../cohorts/{project_id}/cohort-profile/{cohort_id}/draft`
 * which delegates to the same `cohort_draft_service.discard_draft` the legacy
 * socket handler used, so the explicit_save / Snowflake branching is preserved.
 */
export const useDiscardDraftMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<DiscardDraftResponse, Error, DiscardDraftInput>({
    mutationKey: [COHORT_WRITE_MUTATION_KEY],
    mutationFn: async ({ projectId, cohortId }) => {
      const url = `/${COHORTS_PATH}/${projectId}/cohort-profile/${cohortId}/draft`;
      const response = await authFetch<ObjectResponse<DiscardDraftResponse>>(url, {
        method: 'DELETE',
      });
      return response.data;
    },
    onSuccess: (_data, { projectId, cohortId }) => {
      void invalidateCohort(queryClient, { cohortId, projectId });
    },
  });
};
